/**
 * ==========================================================================
 *  PasswortZuruecksetzen.jsx
 * ==========================================================================
 *  Diese Seite wird aufgerufen, wenn jemand auf den Link in der
 *  Passwort-Reset-Mail klickt: https://.../passwort-zuruecksetzen?token=XYZ
 *
 *  - Liest den Token aus der URL
 *  - Lässt den User ein neues Passwort setzen (zweimal zur Bestätigung)
 *  - Schickt das ans Backend (/api/auth/passwort-zuruecksetzen)
 *  - Bei Erfolg: Hinweis, dann zurück zum Login
 * ==========================================================================
 */

import { useState, useEffect } from 'react';
import { Loader2, CheckCircle, Lock, AlertCircle } from 'lucide-react';
import { api, ApiError } from '../api';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0' };

export default function PasswortZuruecksetzen() {
  const [token, setToken] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [fehler, setFehler] = useState('');
  const [erfolg, setErfolg] = useState(false);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get('token') || '');
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setFehler('');
    if (pw1.length < 8) {
      setFehler('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (pw1 !== pw2) {
      setFehler('Die beiden Passwörter stimmen nicht überein.');
      return;
    }
    setLaedt(true);
    try {
      await api.post('/api/auth/passwort-zuruecksetzen', {
        token,
        neues_passwort: pw1,
      });
      setErfolg(true);
    } catch (err) {
      if (err instanceof ApiError && err.detail) {
        setFehler(err.detail);
      } else {
        setFehler('Leider hat das nicht geklappt. Bitte neuen Link anfordern.');
      }
    } finally {
      setLaedt(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url('/hintergrund.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(135deg, ${CI.brombeer} 0%, ${CI.orchidee} 100%)`, zIndex: -1 }}
      />
      <div className="absolute inset-0 bg-slate-900/25" />

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8 text-center" style={{ backgroundColor: CI.brombeer }}>
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Lock className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Neues Passwort setzen</h1>
          </div>

          <div className="p-8">
            {!token ? (
              <div className="text-center py-6">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-gray-700">
                  Kein gültiger Link. Bitte fordern Sie einen neuen Reset-Link an.
                </p>
                <a
                  href="/"
                  className="inline-block mt-6 py-2 px-6 rounded-lg font-bold text-white"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  Zum Login
                </a>
              </div>
            ) : erfolg ? (
              <div className="text-center py-6">
                <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-800 mb-2">Passwort geändert</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
                </p>
                <a
                  href="/"
                  className="inline-block py-2 px-6 rounded-lg font-bold text-white"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  Zum Login
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Neues Passwort
                  </label>
                  <input
                    type="password"
                    required
                    autoComplete="new-password"
                    minLength={8}
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    placeholder="Mindestens 8 Zeichen"
                    className="w-full py-3 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': CI.brombeer }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Neues Passwort wiederholen
                  </label>
                  <input
                    type="password"
                    required
                    autoComplete="new-password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="w-full py-3 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': CI.brombeer }}
                  />
                </div>

                {fehler && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{fehler}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={laedt}
                  className="w-full py-3 rounded-lg font-bold text-white shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  {laedt ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Passwort speichern'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
